import { DocumentType, graphql } from '../gql';
import { useApiMutation, useApiQuery } from '../query/hooks';
import { Grid, InputLabel, Stack } from '@mui/material';
import { FC, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { z } from 'zod';
import { ModalContent } from '../modal/components/ModalContent';
import { ModalActions } from '../modal/components/ModalActions';
import { useModalContext } from '../modal/ModalContext';
import { mutationResponseHandler } from '../utils/mutationResponseHandler';
import { CreatableSelect } from '../connections/metrics/CreatableSelect';
import { useSnackbar } from '../store/snackbar';

const opportunityAddActionModal_listActions = graphql(`
	query opportunityAddActionModal_listActions {
		listAction(filter: { status: { notEq: resolved } }) {
			id
			name
		}
	}
`);

const opportunityAddActionModal_getOpportunity = graphql(`
	query opportunityAddActionModal_getOpportunity($id: UUID!) {
		getOpportunity(by: { id: $id }) {
			actions {
				id
			}
		}
	}
`);

const opportunityAddActionModal_createAction = graphql(`
	mutation opportunityAddActionModal_createAction(
		$name: String!
		$description: String
		$priority: ActionPriority!
		$status: ActionStatus!
		$links: Json!
		$activities: [ActionCreateActivitiesEntityRelationInput!]
	) {
		createAction(
			data: { name: $name, description: $description, priority: $priority, status: $status, links: $links, activities: $activities }
		) {
			ok
			errorMessage
			node {
				id
			}
		}
	}
`);

const opportunityAddActionModal_updateOpportunityActions = graphql(`
	mutation opportunityAddActionModal_updateOpportunityActions($opportunityId: UUID!, $actionId: UUID!) {
		updateOpportunity(by: { id: $opportunityId }, data: { actions: { connect: { id: $actionId } } }) {
			ok
			errorMessage
		}
	}
`);

export const addActionModalSchema = z.object({
	opportunityId: z.string(),
	activityId: z.string().optional(),
});

export const AddActionModal: FC<z.infer<typeof addActionModalSchema>> = ({ opportunityId, activityId }) => {
	const intl = useIntl();
	const { closeModal } = useModalContext();
	const { openSnackbar } = useSnackbar();

	const { isLoading: isActionsLoading, data: actionsData } = useApiQuery(opportunityAddActionModal_listActions);
	const { isLoading: isOpportunityLoading, data: opportunityData } = useApiQuery(opportunityAddActionModal_getOpportunity, {
		variables: { id: opportunityId },
	});
	const { mutate: createAction, isPending: isCreatePending } = useApiMutation(opportunityAddActionModal_createAction);
	const { mutate: updateOpportunityActions, isPending: isUpdatePending } = useApiMutation(
		opportunityAddActionModal_updateOpportunityActions,
	);

	const [actionIdValue, setActionIdValue] = useState<string>('');

	const removedIds = opportunityData?.getOpportunity?.actions.map((s) => s.id) ?? [];
	const options = (actionsData?.listAction ?? [])
		.filter((s) => !isOpportunityLoading && !removedIds.includes(s.id))
		.map((s) => ({ label: s.name, value: s.id }));

	const isLoading = isActionsLoading || isOpportunityLoading;
	const submitDisabled = actionIdValue === '' || isLoading || isCreatePending || isUpdatePending;

	const onSubmit = () => {
		updateOpportunityActions(
			{
				opportunityId,
				actionId: actionIdValue,
			},
			{
				onSuccess: (result) => {
					mutationResponseHandler({
						result: result.updateOpportunity.ok,
						toastMessages: {
							success: intl.formatMessage({ id: 'opportunity.add-action.update.success' }),
							error: intl.formatMessage({ id: 'opportunity.add-action.update.error' }),
						},
						callback: () => closeModal(),
						openSnackbar,
					});
				},
			},
		);
	};

	const handleOnCreate = (name: string) => {
		createAction(
			{
				name,
				description: '',
				priority: 'medium',
				status: 'open',
				links: { values: [] },
				...(activityId != null && {
					activities: {
						connect: {
							id: activityId,
						},
					},
				}),
			},
			{
				onSuccess: handleSuccessfulCreate,
			},
		);
	};

	const handleSuccessfulCreate = (data: DocumentType<typeof opportunityAddActionModal_createAction>) => {
		if (data.createAction.ok) {
			setActionIdValue(data.createAction.node?.id ?? '');
		} else {
			openSnackbar({
				open: true,
				message: intl.formatMessage({ id: 'action.list.create.error' }),
				variant: 'alert',
				alert: {
					color: 'error',
				},
				close: true,
			});
		}
	};

	return (
		<>
			<ModalContent title={intl.formatMessage({ id: 'opportunity.add-action.title' })}>
				<Grid container spacing={3}>
					<Grid item xs={12}>
						<Stack spacing={1}>
							<InputLabel>
								<FormattedMessage id={'opportunity.add-action.form.label'} />
							</InputLabel>
							<CreatableSelect
								onChange={setActionIdValue}
								loading={isLoading || isCreatePending}
								value={actionIdValue}
								items={options}
								createItemTitle={intl.formatMessage({ id: 'opportunity.add-action.form.create-placeholder' })}
								createDialogTitle={intl.formatMessage({ id: 'opportunity.add-action.popover.title' })}
								emptyItemErrorMessage={intl.formatMessage({ id: 'opportunity.add-action.popover.error.empty' })}
								placeholder={intl.formatMessage({ id: 'opportunity.add-action.form.select-placeholder' })}
								onCreate={handleOnCreate}
							/>
						</Stack>
					</Grid>
				</Grid>
			</ModalContent>
			<ModalActions
				handleCancelButtonClick={closeModal}
				handlePrimaryButtonClick={onSubmit}
				cancelButtonText={intl.formatMessage({ id: 'common.cancel' })}
				primaryButtonText={intl.formatMessage({ id: 'common.add' })}
				disablePrimaryButton={submitDisabled}
			/>
		</>
	);
};
